import { DealersByRegion, PPNDealer, PPNDealerSearchResult, PPNDistance } from '../entities/dealer-response';
import { Environment, Environments } from '../entities/environment';
import { LatLong } from '../entities/places';
import { NaviError, NaviErrorTypes } from '../utility/navi-error';
import windowService from './window-service';
import { navStateStore } from '../state/nav-state-store';
import { getConfig, getQueryString } from '../utility/helper';

export async function getAllPPNDealersForMarket(
  env: Environment,
  dealerApiUrl: string,
  country: string
): Promise<PPNDealerSearchResult[]> {
  return getPPNDealers(env, dealerApiUrl, { latitude: 0, longitude: 0 }, country, { radius: 10000000 });
}

type GetPPNDealersOptions = {
  /**
   * The radius around the given coordinates to search for dealers
   */
  radius?: number;
};

export async function getPPNDealers(
  env: Environment,
  dealerApiUrl: string,
  coordinates: LatLong,
  country: string,
  options: GetPPNDealersOptions = {}
): Promise<PPNDealerSearchResult[]> {
  const { radius } = options;

  let response: Response;
  let ppnDealerSearchResults: { dealer: PPNDealer; distance: PPNDistance }[];

  try {
    const headers = new Headers();

    headers.set('Accept', '*/*');

    /* istanbul ignore next */
    if (env === Environments.LOCAL) {
      headers.set(
        'Authorization',
        'Basic ' + windowService.btoa(`${'DEALER_SEARCH_API_USER'}:${'DEALER_SEARCH_API_PASS'}`)
      );
    }

    const requestParams = {
      coordinates: `${coordinates.latitude},${coordinates.longitude}`,
      radius
    };

    response = await fetch(`${dealerApiUrl}/${country}/dealers?${getQueryString(requestParams)}`, {
      method: 'GET',
      headers
    });
  } catch (err) {
    throw new NaviError('Unable to fetch dealers', NaviErrorTypes.GENERAL);
  }

  if (response.status === 400) {
    throw new NaviError('dealerService.getPPNDealers failed with invalid coordinates', NaviErrorTypes.GENERAL);
  }

  if (response.status === 404) {
    throw new NaviError(
      'dealerService.getPPNDealers failed with invalid country code and therefore responded with no results',
      NaviErrorTypes.NO_RESULTS
    );
  }

  if (response.status !== 200) {
    throw new NaviError('dealerService.getPPNDealers failed with invalid response', NaviErrorTypes.GENERAL);
  }

  try {
    ppnDealerSearchResults = await response.json();
  } catch (err) {
    throw new NaviError(
      'dealerService.getPPNDealers could not parse response into json format',
      NaviErrorTypes.GENERAL
    );
  }

  return ppnDealerSearchResults.map(({ dealer, ...rest }) => ({ ppnDealer: dealer, ...rest }));
}

export async function getPPNDealer(
  env: Environment,
  dealerApiUrl: string,
  id: string,
  coordinates: LatLong,
  country: string
): Promise<PPNDealerSearchResult> {
  // (LR/MX) 13.09.2022: If we ever remove the workaround, remember to map the dealer type to ppnDealer
  let ppnDealer: PPNDealerSearchResult | undefined;

  try {
    const ppnDealerSearchResults = await getPPNDealers(env, dealerApiUrl, coordinates, country);
    ppnDealer = ppnDealerSearchResults.find((dealerSearchResult) => id === dealerSearchResult.ppnDealer.id);
  } catch (err) {
    throw new NaviError('Unable to fetch a dealer', NaviErrorTypes.GENERAL);
  }

  if (ppnDealer === undefined) {
    throw new NaviError(`Requested dealer ${id} could not be found`, NaviErrorTypes.NO_RESULTS);
  }

  return ppnDealer;
}

const dealersByRegionMap = new Map<string, DealersByRegion | undefined>();

export async function getPPNDealerByRegion(id: string, region: string): Promise<PPNDealerSearchResult> {
  const dealersOfRegion = await getPPNDealersByRegion(region);

  const dealerResult = dealersOfRegion.regions
    .flatMap(({ cities }) =>
      cities.flatMap(({ dealers }) =>
        Object.values(dealers).reduce<{ ppnDealer: PPNDealer }[]>((dealerList, dealer) => [...dealerList, dealer], [])
      )
    )
    .find(({ ppnDealer }) => ppnDealer.id === id);

  if (dealerResult === undefined) {
    throw new NaviError(`Requested dealer could not be found`, NaviErrorTypes.NO_RESULTS);
  }

  return dealerResult;
}

export async function getPPNDealersByRegion(region: string): Promise<DealersByRegion> {
  const { env } = navStateStore.state;
  const headers = new Headers();
  let dealersByRegion: DealersByRegion | undefined;
  if (dealersByRegionMap.has(region)) {
    dealersByRegion = dealersByRegionMap.get(region);
  } else {
    const { BFF_ENDPOINT } = getConfig(env);

    headers.set('Accept', '*/*');

    const response = await fetch(`${BFF_ENDPOINT}/dealers/region/${region}?env=${env}`, {
      method: 'GET',
      headers
    });

    dealersByRegion = await response.json();
    dealersByRegionMap.set(region, dealersByRegion);
  }
  if (dealersByRegion === undefined) {
    throw new NaviError(`Requested dealer could not be found`, NaviErrorTypes.NO_RESULTS);
  }
  return dealersByRegion;
}
